import React, { useEffect } from 'react';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import { Col, Row } from 'react-bootstrap';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import styles from 'views/MockServices/Tabs/MockServices/MockServices.module.css';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import { ServiceType } from 'components/modal/wizard/WizardTransactionTypes';
import Autofocus from '../../forms/Autofocus/Autofocus';

interface ServiceProps {
  idOrName: ServiceType;
  service: number | string;
  services: Service[];
  handleServiceChange: (service: number) => void;
  setServiceState: (idOrName: ServiceType, service: number | string, isValid: boolean) => void;
}

const ServiceWizard = (props: ServiceProps) => {
  const handleServiceChange = (service: Service | null) => {
    if (service !== null) {
      props.handleServiceChange(service.id as number);
    }
  };

  const nameInput = useFormInput(
    'init',
    (fn: CallbackFn) => {
      props.setServiceState('new', fn.value, fn.isValid);
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('servicePicker.serviceNameIsRequired'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('servicePicker.serviceNameMaxLength', { maxLength: 250 }),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('servicePicker.serviceNameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('servicePicker.serviceNameIsInvalid'),
    },
    {
      type: ValidatorType.VALUE_FOUND_ON_ARRAY,
      value: props.services.map(s => s.name),
      invalidMessage: i18n.t('servicePicker.serviceNameAlreadyExists'),
    },
  );

  useEffect(() => {
    if (props.idOrName === 'new' && nameInput.value !== props.service) {
      nameInput.resetValue(props.service);
    }
  }, [props.service]);

  const serviceTitle = props.idOrName === 'byId' ? 'Service' : i18n.t('common:servicePicker.serviceName');

  return (
    <div>
      <h2 className={'mb-5'}>{i18n.t('wizard:details.service.title')}</h2>

      <p>{i18n.t('wizard:details.service.description1')}</p>
      <p className={'mb-5'}>{i18n.t('wizard:details.service.description2')}</p>

      <div>
        <Row>
          <Col md={4}>
            <div style={{ width: '250px' }}>
              <label
                className="mr-3 mb-1 font-weight-bold"
                htmlFor="wizardService"
                aria-describedby="wizardServicePicker"
              >
                {serviceTitle}
                <span id="mockServicesHelpId" className={styles.questionNearTitle} title={i18n.t('common:showHelp')}>
                  <HelpTooltip id={'Service_MockService_table'} />
                </span>
              </label>

              {props.idOrName === 'byId' ? (
                <ServicePicker
                  preSelected={props.service as number}
                  selectHandler={handleServiceChange}
                  isSelectAll={true}
                  isCreateEnabled={false}
                  id="wizardService"
                  isMaintenanceEnabled={false}
                  isCloneEnabled={false}
                  copyServiceIdCapability={false}
                />
              ) : (
                <>
                  <Autofocus>
                    <BzFormControlWithHook
                      id={'wizardService'}
                      autoComplete="off"
                      useFormInputHook={nameInput}
                      placeholder={i18n.t('common:servicePicker.serviceName')}
                      required={true}
                      hideErrors={true}
                    />
                  </Autofocus>
                  <div className="text-muted small mt-2">{i18n.t('wizard:details.service.inputDescription')}</div>
                  {!nameInput.valid && <div className="invalid-feedback"> {nameInput.invalidMessage}</div>}
                </>
              )}
            </div>
          </Col>
          <Col md={4}>
            <div>
              {/*code to be uncomment in the next version of this wizard*/}
              {/*<Button variant="link" onClick={() => {*/}
              {/*  props.setServiceState(props.idOrName === 'byId' ? 'new' : 'byId', '')*/}
              {/*}}>*/}
              {/*  {props.idOrName === 'byId' ? (*/}
              {/*      i18n.t('wizard:details.service.createNewService'))*/}
              {/*    : (*/}
              {/*      i18n.t('wizard:details.service.useExistingService')*/}
              {/*    )}*/}
              {/*</Button>*/}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ServiceWizard;
